import React, { useEffect, useState } from 'react';
import { ChevronRightIcon, BookIcon, DatabaseIcon, FilterIcon, RefreshCwIcon, SearchIcon, CalendarIcon, ArrowDownIcon, ArrowUpIcon, XIcon } from 'lucide-react';
import { LedgerTable } from './LedgerTable';
import { LedgerEntryDetail } from './LedgerEntryDetail';
import { formatCurrency } from '../utils/currencyFormatter';
// Define types for the ledger entry
export type LedgerEntryStatus = 'Draft' | 'Posted' | 'Reversed';
export type LedgerSourceModule = 'RapidAP' | 'RapidPO' | 'RapidExpense' | 'RapidContract' | 'RapidPayment' | 'RapidBudget' | 'Manual';
export type LedgerEntry = {
  id: string;
  date: string;
  sourceModule: LedgerSourceModule;
  sourceReference: string;
  description: string;
  glCode: string;
  debit: number | null;
  credit: number | null;
  currency: string;
  status: string;
};
export function LedgerView({
  locale = 'en-US'
}) {
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<LedgerEntry | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    sourceModule: [] as LedgerSourceModule[],
    glCode: '',
    status: [] as LedgerEntryStatus[],
    currency: '',
    searchTerm: ''
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Fetch sample ledger data
  useEffect(() => {
    // In a real application, this would be an API call
    const sampleData: LedgerEntry[] = [{
      id: 'LE-2023-001',
      date: '2023-10-15',
      description: 'Invoice payment to Office Depot',
      glCode: '1000.1100.1110',
      glPath: 'Assets.Current Assets.Cash and Cash Equivalents',
      debit: null,
      credit: 1250.75,
      currency: 'USD',
      exchangeRate: null,
      sourceModule: 'RapidAP',
      sourceReference: 'INV-2023-001',
      vendor: 'Office Depot',
      entity: 'Corporate',
      site: 'Headquarters',
      status: 'Posted',
      postedBy: 'John Smith',
      postedDate: '2023-10-16',
      notes: 'Payment for office supplies'
    }, {
      id: 'LE-2023-002',
      date: '2023-10-15',
      description: 'Invoice payment to Office Depot',
      glCode: '5000.5100.5110',
      glPath: 'Expenses.Operating Expenses.Office Supplies',
      debit: 1250.75,
      credit: null,
      currency: 'USD',
      exchangeRate: null,
      sourceModule: 'RapidAP',
      sourceReference: 'INV-2023-001',
      vendor: 'Office Depot',
      entity: 'Corporate',
      site: 'Headquarters',
      status: 'Posted',
      postedBy: 'John Smith',
      postedDate: '2023-10-16'
    }, {
      id: 'LE-2023-003',
      date: '2023-10-12',
      description: 'Purchase Order for IT Equipment',
      glCode: '2000.2100.2110',
      glPath: 'Liabilities.Current Liabilities.Accounts Payable',
      debit: null,
      credit: 5430.0,
      currency: 'USD',
      exchangeRate: null,
      sourceModule: 'RapidPO',
      sourceReference: 'PO-2023-002',
      vendor: 'Dell Technologies',
      entity: 'Corporate',
      site: 'Headquarters',
      status: 'Posted',
      postedBy: 'Jane Doe',
      postedDate: '2023-10-12'
    }, {
      id: 'LE-2023-004',
      date: '2023-10-12',
      description: 'Purchase Order for IT Equipment',
      glCode: '1000.1200.1210',
      glPath: 'Assets.Non-Current Assets.Equipment',
      debit: 5430.0,
      credit: null,
      currency: 'USD',
      exchangeRate: null,
      sourceModule: 'RapidPO',
      sourceReference: 'PO-2023-002',
      vendor: 'Dell Technologies',
      entity: 'Corporate',
      site: 'Headquarters',
      status: 'Posted',
      postedBy: 'Jane Doe',
      postedDate: '2023-10-12'
    }, {
      id: 'LE-2023-005',
      date: '2023-10-10',
      description: 'Travel expense reimbursement',
      glCode: '5000.5200.5210',
      glPath: 'Expenses.Travel Expenses.Airfare',
      debit: 850.25,
      credit: null,
      currency: 'EUR',
      exchangeRate: 1.08,
      sourceModule: 'RapidExpense',
      sourceReference: 'EXP-2023-015',
      entity: 'Corporate',
      site: 'European Office',
      status: 'Posted',
      postedBy: 'Maria Rodriguez',
      postedDate: '2023-10-11'
    }, {
      id: 'LE-2023-006',
      date: '2023-10-10',
      description: 'Travel expense reimbursement',
      glCode: '1000.1100.1110',
      glPath: 'Assets.Current Assets.Cash and Cash Equivalents',
      debit: null,
      credit: 850.25,
      currency: 'EUR',
      exchangeRate: 1.08,
      sourceModule: 'RapidExpense',
      sourceReference: 'EXP-2023-015',
      entity: 'Corporate',
      site: 'European Office',
      status: 'Posted',
      postedBy: 'Maria Rodriguez',
      postedDate: '2023-10-11'
    }, {
      id: 'LE-2023-007',
      date: '2023-10-05',
      description: 'Contract payment for software license',
      glCode: '5000.5300.5310',
      glPath: 'Expenses.Software Expenses.Licenses',
      debit: 12000.0,
      credit: null,
      currency: 'USD',
      exchangeRate: null,
      sourceModule: 'RapidContract',
      sourceReference: 'CON-2023-008',
      vendor: 'Microsoft Corporation',
      entity: 'Corporate',
      site: 'Headquarters',
      status: 'Posted',
      postedBy: 'Robert Johnson',
      postedDate: '2023-10-05'
    }, {
      id: 'LE-2023-008',
      date: '2023-10-05',
      description: 'Contract payment for software license',
      glCode: '1000.1100.1110',
      glPath: 'Assets.Current Assets.Cash and Cash Equivalents',
      debit: null,
      credit: 12000.0,
      currency: 'USD',
      exchangeRate: null,
      sourceModule: 'RapidContract',
      sourceReference: 'CON-2023-008',
      vendor: 'Microsoft Corporation',
      entity: 'Corporate',
      site: 'Headquarters',
      status: 'Posted',
      postedBy: 'Robert Johnson',
      postedDate: '2023-10-05'
    }, {
      id: 'LE-2023-009',
      date: '2023-10-18',
      description: 'Manual adjustment - Accrual for utilities',
      glCode: '5000.5400.5410',
      glPath: 'Expenses.Utility Expenses.Electricity',
      debit: 750.0,
      credit: null,
      currency: 'USD',
      exchangeRate: null,
      sourceModule: 'Manual',
      sourceReference: 'ADJ-2023-042',
      entity: 'Corporate',
      site: 'Headquarters',
      status: 'Draft',
      notes: 'Monthly accrual for electricity bill'
    }, {
      id: 'LE-2023-010',
      date: '2023-10-18',
      description: 'Manual adjustment - Accrual for utilities',
      glCode: '2000.2100.2120',
      glPath: 'Liabilities.Current Liabilities.Accrued Expenses',
      debit: null,
      credit: 750.0,
      currency: 'USD',
      exchangeRate: null,
      sourceModule: 'Manual',
      sourceReference: 'ADJ-2023-042',
      entity: 'Corporate',
      site: 'Headquarters',
      status: 'Draft',
      notes: 'Monthly accrual for electricity bill'
    }, {
      id: 'LE-2023-011',
      date: '2023-10-03',
      description: 'Reversal of incorrect expense entry',
      glCode: '5000.5100.5120',
      glPath: 'Expenses.Operating Expenses.Maintenance',
      debit: null,
      credit: 325.5,
      currency: 'USD',
      exchangeRate: null,
      sourceModule: 'Manual',
      sourceReference: 'REV-2023-007',
      entity: 'Corporate',
      site: 'Headquarters',
      status: 'Reversed',
      postedBy: 'Sarah Williams',
      postedDate: '2023-10-03',
      notes: 'Reversing incorrect maintenance expense'
    }, {
      id: 'LE-2023-012',
      date: '2023-10-03',
      description: 'Reversal of incorrect expense entry',
      glCode: '1000.1100.1110',
      glPath: 'Assets.Current Assets.Cash and Cash Equivalents',
      debit: 325.5,
      credit: null,
      currency: 'USD',
      exchangeRate: null,
      sourceModule: 'Manual',
      sourceReference: 'REV-2023-007',
      entity: 'Corporate',
      site: 'Headquarters',
      status: 'Reversed',
      postedBy: 'Sarah Williams',
      postedDate: '2023-10-03',
      notes: 'Reversing incorrect maintenance expense'
    }];
    setEntries(sampleData);
  }, []);
  // Handle selecting an entry
  const handleSelectEntry = (entry: LedgerEntry) => {
    setSelectedEntry(entry);
    setIsDetailOpen(true);
  };
  // Handle closing the detail drawer
  const handleCloseDetail = () => {
    setIsDetailOpen(false);
  };
  // Apply filters to entries
  const filteredEntries = entries.filter(entry => {
    // Date filter
    if (filters.dateFrom && new Date(entry.date) < new Date(filters.dateFrom)) {
      return false;
    }
    if (filters.dateTo && new Date(entry.date) > new Date(filters.dateTo)) {
      return false;
    }
    // Source module filter
    if (filters.sourceModule.length > 0 && !filters.sourceModule.includes(entry.sourceModule)) {
      return false;
    }
    // GL Code filter
    if (filters.glCode && !entry.glCode.includes(filters.glCode)) {
      return false;
    }
    // Status filter
    if (filters.status.length > 0 && !filters.status.includes(entry.status)) {
      return false;
    }
    // Currency filter
    if (filters.currency && entry.currency !== filters.currency) {
      return false;
    }
    // Search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      return entry.description.toLowerCase().includes(searchLower) || entry.sourceReference.toLowerCase().includes(searchLower) || entry.glPath.toLowerCase().includes(searchLower) || entry.vendor && entry.vendor.toLowerCase().includes(searchLower);
    }
    return true;
  });
  // Calculate totals for the filtered entries
  const totalDebits = filteredEntries.reduce((sum, entry) => sum + (entry.debit || 0), 0);
  const totalCredits = filteredEntries.reduce((sum, entry) => sum + (entry.credit || 0), 0);
  const netBalance = totalDebits - totalCredits;
  // Handle filter changes
  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      sourceModule: [],
      glCode: '',
      status: [],
      currency: '',
      searchTerm: ''
    });
  };
  // Toggle a multi-select filter
  const toggleFilter = (filterName: 'sourceModule' | 'status', value: any) => {
    setFilters(prev => {
      const currentValues = prev[filterName];
      return {
        ...prev,
        [filterName]: currentValues.includes(value) ? currentValues.filter(v => v !== value) : [...currentValues, value]
      };
    });
  };
  return <div className="space-y-6" data-id="element-8304">
      <div className="mb-4" data-id="element-8305">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-8306">Financial Ledger</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-8307">
          View and track all financial transactions across the procure-to-pay
          lifecycle
        </p>
      </div>

      {/* Breadcrumbs */}
      <nav className="flex mb-4" aria-label="Breadcrumb" data-id="element-8308">
        <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-8309">
          <li className="inline-flex items-center" data-id="element-8310">
            <button className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800" data-id="element-8311">
              <BookIcon className="w-4 h-4 mr-2" data-id="element-8312" />
              Financial
            </button>
          </li>
          <li data-id="element-8313">
            <div className="flex items-center" data-id="element-8314">
              <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-8315" />
              <span className="text-sm font-medium text-gray-500" data-id="element-8316">Ledger</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6" data-id="element-8317">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-8318">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-8319">
            <DatabaseIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-8320" />
            Ledger Entries
          </h3>
          <div className="flex items-center space-x-2" data-id="element-8321">
            <div className="relative rounded-md shadow-sm" data-id="element-8322">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8323">
                <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-8324" />
              </div>
              <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search ledger entries..." value={filters.searchTerm} onChange={e => handleFilterChange('searchTerm', e.target.value)} data-id="element-8325" />
            </div>
            <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8326">
              <FilterIcon className="h-4 w-4 mr-1" data-id="element-8327" />
              Filters
            </button>
            <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8328">
              <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-8329" />
              Reset
            </button>
          </div>
        </div>

        {/* Expanded filters */}
        {isFilterOpen && <div className="px-4 py-5 sm:px-6" data-id="element-8330">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-8331">
              {/* Date range */}
              <div className="sm:col-span-2" data-id="element-8332">
                <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700" data-id="element-8333">
                  Date From
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-8334">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8335">
                    <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-8336" />
                  </div>
                  <input type="date" id="dateFrom" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={filters.dateFrom} onChange={e => handleFilterChange('dateFrom', e.target.value)} data-id="element-8337" />
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-8338">
                <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700" data-id="element-8339">
                  Date To
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-8340">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8341">
                    <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-8342" />
                  </div>
                  <input type="date" id="dateTo" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={filters.dateTo} onChange={e => handleFilterChange('dateTo', e.target.value)} data-id="element-8343" />
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-8344">
                <label htmlFor="glCode" className="block text-sm font-medium text-gray-700" data-id="element-8345">
                  GL Code
                </label>
                <div className="mt-1" data-id="element-8346">
                  <input type="text" id="glCode" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="e.g. 1000.1100" value={filters.glCode} onChange={e => handleFilterChange('glCode', e.target.value)} data-id="element-8347" />
                </div>
              </div>
              {/* Source Module */}
              <div className="sm:col-span-3" data-id="element-8348">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8349">
                  Source Module
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-8350">
                  {['RapidAP', 'RapidPO', 'RapidExpense', 'RapidContract', 'RapidPayment', 'RapidBudget', 'Manual'].map(module => <button key={module} type="button" onClick={() => toggleFilter('sourceModule', module)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${filters.sourceModule.includes(module as LedgerSourceModule) ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-8351">
                      {module}
                    </button>)}
                </div>
              </div>
              {/* Status */}
              <div className="sm:col-span-3" data-id="element-8352">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8353">
                  Status
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-8354">
                  {['Draft', 'Posted', 'Reversed'].map(status => <button key={status} type="button" onClick={() => toggleFilter('status', status)} className={`inline-flex items-center px-2.5 py-1.5 border text-xs font-medium rounded ${filters.status.includes(status as LedgerEntryStatus) ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-8355">
                      {status}
                    </button>)}
                </div>
              </div>
              {/* Currency */}
              <div className="sm:col-span-2" data-id="element-8356">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-8357">
                  Currency
                </label>
                <select id="currency" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.currency} onChange={e => handleFilterChange('currency', e.target.value)} data-id="element-8358">
                  <option value="" data-id="element-8359">All Currencies</option>
                  <option value="USD" data-id="element-8360">USD - US Dollar</option>
                  <option value="EUR" data-id="element-8361">EUR - Euro</option>
                  <option value="GBP" data-id="element-8362">GBP - British Pound</option>
                  <option value="CAD" data-id="element-8363">CAD - Canadian Dollar</option>
                  <option value="AUD" data-id="element-8364">AUD - Australian Dollar</option>
                </select>
              </div>
            </div>
          </div>}
      </div>

      {/* Summary panel */}
      <div className="bg-white shadow rounded-lg p-4 mb-6" data-id="element-8365">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-8366">
          <div className="p-4 bg-green-50 rounded-lg" data-id="element-8367">
            <div className="flex justify-between items-center" data-id="element-8368">
              <h4 className="text-sm font-medium text-green-800" data-id="element-8369">
                Total Debits
              </h4>
              <ArrowUpIcon className="h-4 w-4 text-green-600" data-id="element-8370" />
            </div>
            <p className="text-2xl font-mono font-semibold text-green-700" data-id="element-8371">
              {formatCurrency(totalDebits, 'USD', locale)}
            </p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg" data-id="element-8372">
            <div className="flex justify-between items-center" data-id="element-8373">
              <h4 className="text-sm font-medium text-red-800" data-id="element-8374">
                Total Credits
              </h4>
              <ArrowDownIcon className="h-4 w-4 text-red-600" data-id="element-8375" />
            </div>
            <p className="text-2xl font-mono font-semibold text-red-700" data-id="element-8376">
              {formatCurrency(totalCredits, 'USD', locale)}
            </p>
          </div>
          <div className={`p-4 rounded-lg ${Math.abs(netBalance) < 0.01 ? 'bg-green-50' : 'bg-yellow-50'}`} data-id="element-8377">
            <div className="flex justify-between items-center" data-id="element-8378">
              <h4 className={`text-sm font-medium ${Math.abs(netBalance) < 0.01 ? 'text-green-800' : 'text-yellow-800'}`} data-id="element-8379">
                Net Balance
              </h4>
              {Math.abs(netBalance) < 0.01 ? <CheckIcon className="h-4 w-4 text-green-600" data-id="element-8380" /> : <AlertIcon className="h-4 w-4 text-yellow-600" data-id="element-8381" />}
            </div>
            <p className={`text-2xl font-mono font-semibold ${Math.abs(netBalance) < 0.01 ? 'text-green-700' : 'text-yellow-700'}`} data-id="element-8382">
              {formatCurrency(netBalance, 'USD', locale)}
            </p>
          </div>
        </div>
      </div>

      {/* Ledger table */}
      <LedgerTable entries={filteredEntries} onSelectEntry={handleSelectEntry} locale={locale} data-id="element-8383" />

      {/* Entry detail drawer */}
      {isDetailOpen && selectedEntry && <LedgerEntryDetail entry={selectedEntry} onClose={handleCloseDetail} locale={locale} data-id="element-8384" />}
    </div>;
}
// Helper components for the summary panel
const CheckIcon = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-8385">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-8386" />
  </svg>;
const AlertIcon = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-8387">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-8388" />
  </svg>;